import '../App.css';
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production"
    ? "https://hooks-28xd.onrender.com"
    : "http://localhost:4000";

const card = {
    background: "#1a1a2e",
    border: "1px solid #2a2a4a",
    borderRadius: "10px",
    padding: "20px",
};

const STATUS_COLOR = {
    pending: "#f59e0b",
    approved: "#4ecdc4",
    archived: "#555",
};

const TYPE_OPTIONS = ["", "image", "video", "markdown", "text", "json", "other"];
const SOURCE_OPTIONS = ["", "fal", "marketing", "agent", "manual"];
const REVIEW_OPTIONS = ["", "pending", "approved", "archived"];

const Assets = () => {
    const navigate = useNavigate();
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [filterType, setFilterType] = useState("");
    const [filterSource, setFilterSource] = useState("");
    const [filterStatus, setFilterStatus] = useState("");

    // Selected asset for detail view
    const [selected, setSelected] = useState(null);
    const [selectedContent, setSelectedContent] = useState(null);
    const [loadingContent, setLoadingContent] = useState(false);

    // Manual create form
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ name: "", type: "text", storageType: "content", content: "", url: "", description: "" });
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState("");

    const authHeaders = useCallback(async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return { Authorization: `Bearer ${authgear.accessToken}` };
    }, []);

    const fetchAssets = useCallback(async () => {
        setLoading(true);
        try {
            const headers = await authHeaders();
            const params = {};
            if (filterType) params.type = filterType;
            if (filterSource) params.source = filterSource;
            if (filterStatus) params.reviewStatus = filterStatus;
            const { data } = await axios.get(`${base_url}/assets`, { headers, params });
            setAssets(data);
        } catch (_) {}
        finally { setLoading(false); }
    }, [authHeaders, filterType, filterSource, filterStatus]);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        fetchAssets();
    }, [navigate, fetchAssets]);

    const loadDetail = async (asset) => {
        setSelected(asset);
        setSelectedContent(null);
        if (asset.storageType === "content") {
            setLoadingContent(true);
            try {
                const headers = await authHeaders();
                const { data } = await axios.get(`${base_url}/assets/${asset._id}`, { headers });
                setSelectedContent(data.content);
            } catch (_) {}
            finally { setLoadingContent(false); }
        }
    };

    const updateStatus = async (id, reviewStatus) => {
        try {
            const headers = await authHeaders();
            await axios.put(`${base_url}/assets/${id}`, { reviewStatus }, { headers });
            setAssets(prev => prev.map(a => a._id === id ? { ...a, reviewStatus } : a));
            if (selected?._id === id) setSelected(s => ({ ...s, reviewStatus }));
        } catch (_) {}
    };

    const deleteAsset = async (id) => {
        if (!window.confirm("Delete this asset?")) return;
        try {
            const headers = await authHeaders();
            await axios.delete(`${base_url}/assets/${id}`, { headers });
            setAssets(prev => prev.filter(a => a._id !== id));
            if (selected?._id === id) setSelected(null);
        } catch (_) {}
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (creating) return;
        setCreating(true);
        setCreateError("");
        try {
            const headers = await authHeaders();
            const payload = {
                name: form.name.trim(),
                type: form.type,
                storageType: form.storageType,
                description: form.description.trim() || undefined,
                source: "manual",
            };
            if (form.storageType === "content") payload.content = form.content;
            else payload.url = form.url.trim();

            const { data } = await axios.post(`${base_url}/assets`, payload, { headers });
            setAssets(prev => [data, ...prev]);
            setShowCreate(false);
            setForm({ name: "", type: "text", storageType: "content", content: "", url: "", description: "" });
        } catch (err) {
            setCreateError(err.response?.data?.message || err.message);
        } finally {
            setCreating(false);
        }
    };

    const filterSelect = (label, value, setValue, options) => (
        <select
            value={value}
            onChange={e => setValue(e.target.value)}
            style={{
                background: "#0d0d1a", border: "1px solid #2a2a4a", borderRadius: "6px",
                color: value ? "#ddd" : "#555", padding: "6px 10px", fontSize: "12px",
            }}
        >
            <option value="">{label}</option>
            {options.filter(Boolean).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
    );

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "60px 20px 20px", maxWidth: "1100px", margin: "0 auto" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
                    <h2 style={{ color: "#a855f7", margin: 0 }}>Assets</h2>
                    <button
                        onClick={() => setShowCreate(v => !v)}
                        style={{
                            padding: "7px 16px", background: showCreate ? "#2a2a4a" : "transparent",
                            color: showCreate ? "#a855f7" : "#555", border: `1px solid ${showCreate ? "#a855f7" : "#2a2a4a"}`,
                            borderRadius: "6px", cursor: "pointer", fontSize: "13px",
                        }}
                    >
                        + Add Asset
                    </button>
                </div>

                {/* Filters */}
                <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
                    {filterSelect("All types", filterType, setFilterType, TYPE_OPTIONS)}
                    {filterSelect("All sources", filterSource, setFilterSource, SOURCE_OPTIONS)}
                    {filterSelect("All statuses", filterStatus, setFilterStatus, REVIEW_OPTIONS)}
                </div>

                {/* Create form */}
                {showCreate && (
                    <div style={{ ...card, marginBottom: "16px" }}>
                        <div style={{ fontSize: "13px", color: "#888", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.05em" }}>New Asset</div>
                        <form onSubmit={handleCreate} style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <input
                                value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="Name" required
                                style={{ background: "#0d0d1a", border: "1px solid #2a2a4a", borderRadius: "6px", color: "#ddd", padding: "8px 10px", fontSize: "13px" }}
                            />
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                                    style={{ background: "#0d0d1a", border: "1px solid #2a2a4a", borderRadius: "6px", color: "#ddd", padding: "8px 10px", fontSize: "13px" }}>
                                    {TYPE_OPTIONS.filter(Boolean).map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                                <select value={form.storageType} onChange={e => setForm(f => ({ ...f, storageType: e.target.value }))}
                                    style={{ background: "#0d0d1a", border: "1px solid #2a2a4a", borderRadius: "6px", color: "#ddd", padding: "8px 10px", fontSize: "13px" }}>
                                    <option value="content">Store content</option>
                                    <option value="url">URL reference</option>
                                </select>
                            </div>
                            {form.storageType === "content" ? (
                                <textarea
                                    value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                                    placeholder="Paste content here..." rows={8}
                                    style={{ background: "#0d0d1a", border: "1px solid #2a2a4a", borderRadius: "6px", color: "#ddd", padding: "8px 10px", fontSize: "12px", resize: "vertical", lineHeight: 1.6 }}
                                />
                            ) : (
                                <input
                                    value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                                    placeholder="https://..."
                                    style={{ background: "#0d0d1a", border: "1px solid #2a2a4a", borderRadius: "6px", color: "#ddd", padding: "8px 10px", fontSize: "13px" }}
                                />
                            )}
                            <input
                                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                placeholder="Description (optional)"
                                style={{ background: "#0d0d1a", border: "1px solid #2a2a4a", borderRadius: "6px", color: "#ddd", padding: "8px 10px", fontSize: "13px" }}
                            />
                            {createError && <div style={{ fontSize: "12px", color: "#f87171" }}>{createError}</div>}
                            <div>
                                <button type="submit" disabled={creating || !form.name.trim()}
                                    style={{
                                        padding: "8px 20px", background: creating ? "#2a2a4a" : "#a855f7",
                                        color: creating ? "#555" : "#fff", border: "none", borderRadius: "6px",
                                        cursor: creating ? "not-allowed" : "pointer", fontWeight: "bold", fontSize: "13px",
                                    }}
                                >
                                    {creating ? "Creating..." : "Create"}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Main layout: list + detail */}
                <div className="assets-layout">
                    {/* Asset list */}
                    <div className={`assets-list${selected ? " assets-list--narrow" : ""}`}>
                        {loading ? (
                            <div style={{ color: "#555", fontSize: "13px" }}>Loading...</div>
                        ) : assets.length === 0 ? (
                            <div style={{ color: "#555", fontSize: "13px" }}>No assets found.</div>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                {assets.map(a => (
                                    <div
                                        key={a._id}
                                        onClick={() => loadDetail(a)}
                                        style={{
                                            ...card,
                                            padding: "12px 14px",
                                            cursor: "pointer",
                                            borderColor: selected?._id === a._id ? "#a855f7" : "#2a2a4a",
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        {a.storageType === "url" && a.type === "image" && (
                                            <img src={a.url} alt={a.name} style={{ width: 48, height: 48, objectFit: "cover", borderRadius: "4px", flexShrink: 0 }} />
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: "13px", color: "#ddd", fontWeight: "bold", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</div>
                                            <div style={{ display: "flex", gap: "8px", marginTop: "4px", flexWrap: "wrap" }}>
                                                <span style={{ fontSize: "11px", color: "#555" }}>{a.type}</span>
                                                <span style={{ fontSize: "11px", color: "#555" }}>{a.source}</span>
                                                <span style={{ fontSize: "11px", color: STATUS_COLOR[a.reviewStatus] || "#888" }}>{a.reviewStatus}</span>
                                            </div>
                                            <div style={{ fontSize: "11px", color: "#444", marginTop: "2px" }}>
                                                {new Date(a.createdAt).toLocaleString()}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Detail panel */}
                    {selected && (
                        <div className="assets-detail" style={{ ...card }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px", gap: "8px" }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: "15px", color: "#ddd", fontWeight: "bold" }}>{selected.name}</div>
                                    {selected.description && (
                                        <div style={{ fontSize: "12px", color: "#777", marginTop: "4px" }}>{selected.description}</div>
                                    )}
                                </div>
                                <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: "16px", flexShrink: 0 }}>✕</button>
                            </div>

                            <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
                                <span style={{ fontSize: "11px", color: "#555", background: "#1a1a2e", border: "1px solid #2a2a4a", padding: "2px 8px", borderRadius: "4px" }}>{selected.type}</span>
                                <span style={{ fontSize: "11px", color: "#555", background: "#1a1a2e", border: "1px solid #2a2a4a", padding: "2px 8px", borderRadius: "4px" }}>{selected.source}</span>
                                <span style={{ fontSize: "11px", color: STATUS_COLOR[selected.reviewStatus], background: "#1a1a2e", border: "1px solid #2a2a4a", padding: "2px 8px", borderRadius: "4px" }}>{selected.reviewStatus}</span>
                            </div>

                            {/* Preview */}
                            {selected.storageType === "url" && selected.type === "image" && (
                                <img src={selected.url} alt={selected.name} style={{ width: "100%", borderRadius: "6px", marginBottom: "12px", objectFit: "contain", maxHeight: "360px" }} />
                            )}
                            {selected.storageType === "url" && selected.type === "video" && (
                                <video src={selected.url} controls style={{ width: "100%", borderRadius: "6px", marginBottom: "12px" }} />
                            )}
                            {selected.storageType === "url" && !["image", "video"].includes(selected.type) && (
                                <a href={selected.url} target="_blank" rel="noreferrer" style={{ display: "block", fontSize: "12px", color: "#00d4ff", marginBottom: "12px", wordBreak: "break-all" }}>{selected.url}</a>
                            )}
                            {selected.storageType === "content" && (
                                <div style={{ marginBottom: "12px" }}>
                                    {loadingContent ? (
                                        <div style={{ color: "#555", fontSize: "12px" }}>Loading content...</div>
                                    ) : (
                                        <pre style={{
                                            background: "#0d0d1a", border: "1px solid #2a2a4a", borderRadius: "6px",
                                            padding: "12px", fontSize: "12px", color: "#ccc", overflowX: "auto",
                                            whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "400px", overflowY: "auto",
                                            margin: 0, lineHeight: 1.6,
                                        }}>
                                            {selectedContent}
                                        </pre>
                                    )}
                                </div>
                            )}

                            {selected.sourceJobId && (
                                <div style={{ fontSize: "12px", color: "#555", marginBottom: "12px" }}>
                                    Source job: <span style={{ color: "#777" }}>{selected.sourceJobId}</span>
                                </div>
                            )}
                            {selected.metadata && (
                                <div style={{ marginBottom: "12px" }}>
                                    <div style={{ fontSize: "11px", color: "#555", marginBottom: "4px" }}>Metadata</div>
                                    <pre style={{ background: "#0d0d1a", border: "1px solid #2a2a4a", borderRadius: "4px", padding: "8px", fontSize: "11px", color: "#777", margin: 0, overflowX: "auto" }}>
                                        {JSON.stringify(selected.metadata, null, 2)}
                                    </pre>
                                </div>
                            )}

                            {/* Actions */}
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                {selected.reviewStatus !== "approved" && (
                                    <button onClick={() => updateStatus(selected._id, "approved")}
                                        style={{ padding: "6px 14px", background: "transparent", color: "#4ecdc4", border: "1px solid #4ecdc4", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>
                                        Approve
                                    </button>
                                )}
                                {selected.reviewStatus !== "archived" && (
                                    <button onClick={() => updateStatus(selected._id, "archived")}
                                        style={{ padding: "6px 14px", background: "transparent", color: "#888", border: "1px solid #444", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>
                                        Archive
                                    </button>
                                )}
                                {selected.reviewStatus !== "pending" && (
                                    <button onClick={() => updateStatus(selected._id, "pending")}
                                        style={{ padding: "6px 14px", background: "transparent", color: "#f59e0b", border: "1px solid #f59e0b", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>
                                        Mark Pending
                                    </button>
                                )}
                                <button onClick={() => deleteAsset(selected._id)}
                                    style={{ padding: "6px 14px", background: "transparent", color: "#f87171", border: "1px solid #4a2a2a", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>
                                    Delete
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Assets;
